import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useToast } from '@contexts/Toast.context';
import { lockTraining } from '@stores/useTrainingLockStore';
import { createTaxonomyNode } from '@utils/api';
import { lockParentInSummary } from '@routes/userRoutes/objects/objectTree';
import { buildObjectKey } from '@routes/userRoutes/objects/components/objectDetails/objectsUtils';

/**
 * Create a taxonomy object from `ObjectForm`'s values and start its training: the
 * Objects page and the annotation view both create through it (`ObjectForm` asks
 * first). Creating posts to the taxonomy; the new node only appears in the tree once its
 * model is trained.
 *
 * @param {{ onSuccess?: () => void }} [options] - after the create, on top of the toast
 * @returns {{ createFrom: (parent: object, values: object, options?: { onSuccess?: () => void }) => void,
 *   isPending: boolean }} `createFrom` takes the parent the object is trained from,
 *   `ObjectForm`'s `onSubmit` values and, optionally, a callback for this create only
 */
export const useCreateObject = ({ onSuccess } = {}) => {
	const { addToast } = useToast();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		mutationFn: createTaxonomyNode,
		onSuccess: (_, payload) => {
			// The Objects page tree and the picker's per-project taxonomy.
			queryClient.invalidateQueries({ queryKey: ['objectTaxonomy'] });
			queryClient.invalidateQueries({ queryKey: ['taxonomyNodes'] });
			// The training just used the parent's new annotations, but it starts
			// asynchronously, so a refetch now could still say "trainable" and offer a
			// second run. Lock the parent in the cache now, and in the store so every later
			// refetch keeps it locked until the server agrees.
			lockTraining(payload.parent_uuid);
			queryClient.setQueriesData({ queryKey: ['annotationSummary'] }, summary =>
				lockParentInSummary(summary, payload.parent_uuid)
			);
			onSuccess?.();
			// TODO: the design shows a "Training started" modal here; it waits for the node
			// train endpoint, which also brings retraining.
			addToast({
				id: 'object-create',
				bg: 'success',
				title: 'Training and creation started',
				message: `We will notify you when “${payload.name}” is ready to use.`,
			});
		},
		onError: (error, payload) => {
			Sentry.captureException(error, {
				tags: { feature: 'object-create' },
				extra: { parentUuid: payload.parent_uuid },
			});
			addToast({
				id: 'object-create-error',
				bg: 'danger',
				title: 'Could not create the object',
				message: 'Please try again.',
			});
		},
	});

	// Body of POST /taxonomy/nodes.
	const createFrom = (parent, values, { onSuccess: onCreated } = {}) =>
		mutation.mutate(
			{
				name: values.name,
				parent_uuid: parent.uuid,
				key: buildObjectKey(values.name),
				color: values.color,
				description: values.description,
				trained_label: values.trained_label || null,
				// The form's `size` (metres) is the API's `object_size`; left out when empty.
				...(values.size != null ? { object_size: values.size } : {}),
				// Already uploaded by the form; omitted so the node inherits the parent's.
				...(values.thumbnail ? { thumbnail: values.thumbnail } : {}),
			},
			{ onSuccess: onCreated }
		);

	return { createFrom, isPending: mutation.isPending };
};
